import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { PendingCompaniesRequest } from './types'

export const useCompaniesRequestLifecycleStore = defineStore(
  'insights-companies-request-lifecycle',
  () => {
    const pendingCompaniesRequest = ref<PendingCompaniesRequest | null>(null)
    const segmentRunDone = ref(false)
    const inFlightChunkFetches = ref(0)
    const companiesFullyLoaded = ref(false)
    const companiesFullyLoadedAt = ref<number | null>(null)

    function initRequest(requestRef: string): void {
      pendingCompaniesRequest.value = {
        ref: requestRef,
        chunkCount: 0,
        receivedChunkIds: new Set(),
      }
      segmentRunDone.value = false
      inFlightChunkFetches.value = 0
      companiesFullyLoaded.value = false
      companiesFullyLoadedAt.value = null
    }

    function initRetryRequest(requestRef: string): void {
      pendingCompaniesRequest.value = {
        ref: requestRef,
        chunkCount: 0,
        receivedChunkIds: new Set(),
      }
      inFlightChunkFetches.value = 0
      // Reset so maybeMarkFullyLoaded can't fire on chunkCount=0 while the retry is still in flight
      segmentRunDone.value = false
    }

    function setPending({
      requestRef,
      chunkCount,
    }: {
      requestRef: string
      chunkCount: number
    }): void {
      if (pendingCompaniesRequest.value?.ref === requestRef) {
        pendingCompaniesRequest.value.chunkCount = chunkCount
      }
    }

    // Switch the pending request to the canonical ref the backend reports when
    // we generated a new UUID but the backend reused an existing SegmentRun.
    // Without this swap, MessageBus broadcasts (which use the cached SegmentRun's
    // ref) and chunk_results lookups (which find the SegmentRun by ref) drift
    // away from the FE's pending ref and the page hangs at "0% loaded".
    function adoptRequestRef({ from, to }: { from: string; to: string }): void {
      if (pendingCompaniesRequest.value?.ref === from && from !== to) {
        pendingCompaniesRequest.value.ref = to
      }
    }

    function markChunkDone(chunkId: number): void {
      pendingCompaniesRequest.value?.receivedChunkIds.add(chunkId)
    }

    function incrementInFlight(): void {
      inFlightChunkFetches.value++
    }

    function decrementInFlight(): void {
      if (inFlightChunkFetches.value > 0) {
        inFlightChunkFetches.value--
      }
    }

    function markSegmentRunDone(): void {
      segmentRunDone.value = true
    }

    function markFullyLoaded(): void {
      companiesFullyLoaded.value = true
    }

    function setFullyLoadedAt(finishedAt: number | undefined): void {
      companiesFullyLoadedAt.value = finishedAt ?? null
    }

    function reset(): void {
      pendingCompaniesRequest.value = null
      segmentRunDone.value = false
      inFlightChunkFetches.value = 0
      companiesFullyLoaded.value = false
      companiesFullyLoadedAt.value = null
    }

    return {
      pendingCompaniesRequest,
      segmentRunDone,
      inFlightChunkFetches,
      companiesFullyLoaded,
      companiesFullyLoadedAt,
      initRequest,
      initRetryRequest,
      setPending,
      adoptRequestRef,
      markChunkDone,
      incrementInFlight,
      decrementInFlight,
      markSegmentRunDone,
      markFullyLoaded,
      setFullyLoadedAt,
      reset,
    }
  }
)
